import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Linkedin } from 'lucide-react';
import { useEmailSettings } from '../hooks/useEmailSettings';
import { usePublicSiteSettings } from '../hooks/usePublicSiteSettings';
import CustomerTypeSwitcher from './CustomerTypeSwitcher';

interface FooterProps {
  onOpenCustomerTypeModal?: () => void;
}

const Footer: React.FC<FooterProps> = ({ onOpenCustomerTypeModal }) => {
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const { settings } = useEmailSettings();
  const { settings: siteSettings } = usePublicSiteSettings();

  const handleCookieSettingsClick = () => {
    // Clear consent and reload to show the banner again
    localStorage.removeItem('cookieConsent');
    window.location.reload();
  };

  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <img
              src={siteSettings.company_logo_url || "/logo.png"}
              alt="Festo logo"
              className="h-auto w-auto"
            />
            <p className="text-gray-300 mb-4">
              Professionell festutrustning för alla tillfällen. Ljud, ljus och stämning som gör din fest minnesvärd.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/profile.php?id=61577953652127" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-purple-400 transition-colors">
                <Facebook />
              </a>
              <a href="https://www.linkedin.com/company/festutrustning-se/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-purple-400 transition-colors">
                <Linkedin />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4">Länkar</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-purple-400 transition-colors">Hem</Link>
              </li>
              <li>
                <Link to="/produkter" className="text-gray-300 hover:text-purple-400 transition-colors">Produkter</Link>
              </li>
              <li>
                <Link to="/foretag" className="text-gray-300 hover:text-purple-400 transition-colors">Företagsevent</Link>
              </li>
              <li>
                <Link to="/vanliga-fragor" className="text-gray-300 hover:text-purple-400 transition-colors">FAQ</Link>
              </li>
              <li>
                <Link to="/om-oss" className="text-gray-300 hover:text-purple-400 transition-colors">Om oss</Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-gray-300 hover:text-purple-400 transition-colors">Kontakt</Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4">Kontakt</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <MapPin className="mr-2 text-purple-400 shrink-0 mt-1" size={18} />
                <div className="text-gray-300 whitespace-pre-line">
                  {settings.company_name && <div>{settings.company_name}</div>}
                  {settings.company_address && <div>{settings.company_address}</div>}
                  {!settings.company_name && !settings.company_address && <div>—</div>}
                </div>
              </li>
              <li className="flex items-center">
                <Phone className="mr-2 text-purple-400 shrink-0" size={18} />
                <a href={`tel:${settings.company_phone}`} className="text-gray-300 hover:text-purple-400 transition-colors">{settings.company_phone}</a>
              </li>
              <li className="flex items-center">
                <Mail className="mr-2 text-purple-400 shrink-0" size={18} />
                <a href={`mailto:${settings.company_email}`} className="text-gray-300 hover:text-purple-400 transition-colors">{settings.company_email}</a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4">Information</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/villkor" className="text-gray-300 hover:text-purple-400 transition-colors">Allmänna villkor</Link>
              </li>
              <li>
                <Link to="/integritet" className="text-gray-300 hover:text-purple-400 transition-colors">Integritetspolicy</Link>
              </li>
              <li>
                <Link to="/leverantor" className="text-gray-300 hover:text-purple-400 transition-colors">Leverantörsportal</Link>
              </li>
              <li>
                <Link to="/leverantor/registrera" className="text-gray-300 hover:text-purple-400 transition-colors">Bli leverantör</Link>
              </li>
              <li>
                <button
                  onClick={handleCookieSettingsClick}
                  className="text-gray-300 hover:text-purple-400 transition-colors bg-transparent border-none p-0 cursor-pointer"
                >
                  Cookie-inställningar
                </button>
              </li>
              <li>
                <div className="pt-1">
                  <CustomerTypeSwitcher onClick={onOpenCustomerTypeModal} className="w-full justify-between" />
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className="border-t border-gray-800 mt-8 pt-6">
          <div className="text-center">
            <h4 className="text-sm font-medium text-gray-400 mb-4">Vi accepterar följande betalningsmetoder</h4>
            <div className="flex justify-center items-center space-x-6 flex-wrap gap-y-4">
              {/* Visa */}
              <img 
                src={siteSettings.payment_visa_url || "/visacard.png"} 
                alt="Visa" 
                className="h-8 w-auto bg-white rounded px-2 py-1"
              />

              {/* Mastercard */}
              <img 
                src={siteSettings.payment_mastercard_url || "/mastercard.png"} 
                alt="Mastercard" 
                className="h-8 w-auto bg-white rounded px-2 py-1"
              />

              {/* American Express */}
              <img 
                src={siteSettings.payment_amex_url || "/amexcard.png"} 
                alt="American Express" 
                className="h-8 w-auto bg-white rounded px-2 py-1"
              />

              {/* Klarna */}
              <img 
                src={siteSettings.payment_klarna_url || "/klarna.png"} 
                alt="Klarna" 
                className="h-8 w-auto bg-white rounded px-2 py-1"
              />
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} {settings.company_name || 'Webbplats'}. Alla rättigheter förbehållna.</p>
          
          <div className="flex justify-center mt-4 space-x-4">
            <a href="https://www.facebook.com/profile.php?id=61577953652127" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-purple-400 transition-colors">
              <Facebook size={20} />
            </a>
            <a href="https://www.linkedin.com/company/festutrustning-se/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-purple-400 transition-colors">
              <Linkedin size={20} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;